// src/components/drawers/AppDrawer.tsx
//
// The one drawer chrome in the app (plan 2026-10-12-one-drawer-component),
// lifted from the GitHub connection drawer: a header on a hairline (an
// optional mark in a bordered square, an eyebrow, the title, a subtitle,
// badges or actions beside the close button), an optional pinned row, a body,
// and an optional footer that never scrolls away. Presentation only.
//
// Prop names follow AppModal wherever the concept is the same (`leading`,
// `stickyTop`, `bodyPadding`, size tokens), so one vocabulary covers both.
//
// A raw Mantine Drawer outside this folder fails
// `__tests__/drawerGate.test.ts`. If this component cannot express what you
// need, extend it here (or argue for a `DRAWER_EXCEPTIONS` entry) — do not
// reach past it.
//
// LOAD-BEARING, not styling:
//
// 1. The layer, both halves, as in AppModal: match the enclosing layer when
//    no `zIndex` is given, and publish this drawer's layer so a modal opened
//    INSIDE it matches it (ADR-0124, components/modals/ModalLayer.tsx). The
//    provider covers descendants only: an owner that raises the drawer and
//    opens `confirmDialog` from OUTSIDE it passes the same `zIndex` to the
//    confirm itself (AuthorizationDrawer does).
//
// 2. The title goes through `Drawer.Title`. Mantine names the dialog after it
//    only while it is mounted (ModalBaseTitle sets `titleMounted`), so it
//    stays mounted — visually hidden — while `titleEditor` replaces it.
//
// 3. A flex chain, not arithmetic: the content is a column, header / pinned
//    row / footer never shrink, and exactly one box scrolls. With
//    `bodyScroll="body"` that is the padded body; with `"self"` the body is a
//    flex column that does NOT scroll, so the caller's own ScrollArea
//    (`style={{ flex: 1 }}`, `h="100%"`) gets a definite height — the tabbed
//    drawers, whose tab bar sits in `stickyTop`.
//
// 4. Never the plain `style` prop on `Drawer.Content`: Mantine applies it to
//    the content AND the fixed inner wrapper (see AssistantDrawer.tsx).
//    `styles={{ content: … }}` reaches the content only.
//
// 5. The theme's drawer transition and overlay reach this compound drawer
//    through the `DrawerRoot` / `DrawerOverlay` theme keys (theme.tsx), not
//    the `Drawer` key — Mantine resolves defaults per component name.

import type { ReactNode } from "react";
import { Box, Drawer, Group, Text, ThemeIcon, VisuallyHidden } from "@mantine/core";

import { ModalLayerProvider, useModalLayerZIndex } from "@/components/modals/ModalLayer";

/** Widths in px. `narrow` for list/timeline drawers, `wide` for drawers
 *  whose content needs it (a wide table) — a closed set on purpose, since a
 *  width is a decision no gate can see. */
export const APP_DRAWER_SIZES = {
  narrow: 520,
  default: 720,
  wide: 960,
} as const;

export type AppDrawerSize = keyof typeof APP_DRAWER_SIZES;

const HAIRLINE = "1px solid var(--mantine-color-default-border)";

export type AppDrawerProps = {
  opened: boolean;
  onClose: () => void;
  /** The drawer's name: shown large in the header and its accessible name.
   *  Text only and never empty — pass a fallback while the record loads. */
  title: string;
  /** Small, dimmed, above the title (an owner, a parent). */
  eyebrow?: ReactNode;
  /** Small, dimmed, under the title (a type, a status line). */
  subtitle?: ReactNode;
  /** A mark drawn in a bordered square left of the title; no square when absent. */
  leading?: ReactNode;
  /** Badges and header actions (Run now, Resume, Back), left of the close button. */
  headerAside?: ReactNode;
  /** Shown instead of the visible title while renaming in place; the title
   *  stays mounted for the accessible name. */
  titleEditor?: ReactNode;
  /** A pinned row between header and body that never scrolls (a tab bar). */
  stickyTop?: ReactNode;
  /** Actions on a hairline under the body; never scrolls away. */
  footer?: ReactNode;
  size?: AppDrawerSize;
  position?: "left" | "right";
  /** "default": the body has the drawer's padding. "none": the caller's
   *  sections bring their own (the GitHub drawer's collapsible sections). */
  bodyPadding?: "default" | "none";
  /** "body": the body is the one scroller. "self": the body is a flex column
   *  that does not scroll, for a caller with its own ScrollArea. */
  bodyScroll?: "body" | "self";
  zIndex?: number;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  withOverlay?: boolean;
  "data-testid"?: string;
  children: ReactNode;
};

export function AppDrawer({
  opened,
  onClose,
  title,
  eyebrow,
  subtitle,
  leading,
  headerAside,
  titleEditor,
  stickyTop,
  footer,
  size = "default",
  position = "right",
  bodyPadding = "default",
  bodyScroll = "body",
  zIndex,
  closeOnClickOutside,
  closeOnEscape,
  withOverlay = true,
  "data-testid": dataTestId,
  children,
}: AppDrawerProps) {
  const enclosingLayer = useModalLayerZIndex();
  const layer = zIndex ?? enclosingLayer;
  const padded = bodyPadding === "default";

  const visibleTitle = (
    <Text component="span" fz="xl" fw={600} lh={1.25} style={{ wordBreak: "break-word" }}>
      {title}
    </Text>
  );

  return (
    <Drawer.Root
      opened={opened}
      onClose={onClose}
      position={position}
      size={APP_DRAWER_SIZES[size]}
      zIndex={layer}
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      data-testid={dataTestId}
    >
      {withOverlay && <Drawer.Overlay />}
      <Drawer.Content styles={{ content: { display: "flex", flexDirection: "column" } }}>
        <ModalLayerProvider zIndex={layer}>
          <Drawer.Header
            px="xl"
            py="lg"
            style={{ borderBottom: HAIRLINE, alignItems: "flex-start", flexShrink: 0 }}
          >
            <Group gap="md" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
              {leading !== undefined && (
                <ThemeIcon
                  size={44}
                  radius="md"
                  variant="default"
                  aria-hidden
                  style={{ flexShrink: 0 }}
                  data-testid="app-drawer-leading"
                >
                  {leading}
                </ThemeIcon>
              )}
              <Box style={{ flex: 1, minWidth: 0 }}>
                {eyebrow !== undefined && (
                  <Text size="sm" c="dimmed" truncate component="div">
                    {eyebrow}
                  </Text>
                )}
                {titleEditor !== undefined ? (
                  <>
                    <Drawer.Title>
                      <VisuallyHidden>{title}</VisuallyHidden>
                    </Drawer.Title>
                    {titleEditor}
                  </>
                ) : (
                  <Drawer.Title>{visibleTitle}</Drawer.Title>
                )}
                {subtitle !== undefined && (
                  <Text size="sm" c="dimmed" component="div">
                    {subtitle}
                  </Text>
                )}
              </Box>
            </Group>
            <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
              {headerAside}
              <Drawer.CloseButton />
            </Group>
          </Drawer.Header>

          {stickyTop !== undefined && (
            <Box style={{ borderBottom: HAIRLINE, flexShrink: 0 }} data-testid="app-drawer-sticky-top">
              {stickyTop}
            </Box>
          )}

          <Drawer.Body
            p={0}
            style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
          >
            <Box
              data-testid="app-drawer-body"
              px={padded ? "xl" : 0}
              py={padded ? "lg" : 0}
              style={{
                flex: 1,
                minHeight: 0,
                ...(bodyScroll === "body"
                  ? { overflowY: "auto" }
                  : { display: "flex", flexDirection: "column", overflow: "hidden" }),
              }}
            >
              {children}
            </Box>
            {footer !== undefined && (
              <Box
                px="xl"
                py="md"
                data-testid="app-drawer-footer"
                style={{
                  borderTop: HAIRLINE,
                  background: "var(--mantine-color-body)",
                  flexShrink: 0,
                }}
              >
                {footer}
              </Box>
            )}
          </Drawer.Body>
        </ModalLayerProvider>
      </Drawer.Content>
    </Drawer.Root>
  );
}
